// Paramètres de la collectivité : identité, services, numérotation, calendrier, notifications
// et historique des versions (reconstitué depuis le journal des opérations).
import { EtatVide, Onglets, Panneau, Tableau, type ColonneTableau } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { Building, ImageOff, Network } from 'lucide-react';
import { requete } from '../../api/client';
import { useCollectivite } from '../../api/requetes';
import type { EntreeAudit, Liste } from '../../api/types';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { OngletCalendrier, OngletIdentite, OngletNotifications, OngletNumerotation } from './OngletsParametres';
import commun from '../../commun/commun.module.css';
import styles from './administration.module.css';

interface VersionParametre {
  cle?: string;
  version?: number;
  valeur?: unknown;
  date_effet?: string;
  motif?: string;
}

function HistoriqueVersions() {
  const { contexte } = useSession();
  const lisible = peut(contexte, 'audit', 'voir');
  const journal = useQuery({
    queryKey: ['audit', 'parametre'],
    queryFn: ({ signal }) => requete<Liste<EntreeAudit>>('/audit', { signal, parametres: { objet_type: 'parametre', limite: 200 } }).then((r) => r.elements),
    enabled: lisible,
  });
  if (!lisible) {
    return (
      <EtatVide titre="Historique non disponible avec vos droits">
        <p>L&apos;historique des versions provient du journal des opérations, que votre rôle ne permet pas de consulter.</p>
      </EtatVide>
    );
  }
  const colonnes: ColonneTableau<EntreeAudit>[] = [
    { id: 'date', entete: 'Enregistrée le', cellule: (e) => dateHeure(e.horodatage), valeurTri: (e) => e.horodatage, enTeteLigne: true },
    { id: 'cle', entete: 'Paramètre', cellule: (e) => (e.apres as VersionParametre | null)?.cle ?? 'non renseigné' },
    { id: 'version', entete: 'Version', numerique: true, cellule: (e) => (e.apres as VersionParametre | null)?.version ?? '', valeurTri: (e) => (e.apres as VersionParametre | null)?.version ?? null },
    { id: 'effet', entete: 'Date d\'effet', cellule: (e) => dateHeure((e.apres as VersionParametre | null)?.date_effet) },
    { id: 'motif', entete: 'Motif', cellule: (e) => (e.apres as VersionParametre | null)?.motif ?? 'non renseigné' },
    { id: 'valeur', entete: 'Valeur', cellule: (e) => <pre className={styles.json}>{JSON.stringify((e.apres as VersionParametre | null)?.valeur ?? null, null, 2)}</pre> },
  ];
  return (
    <EtatRequete chargement={journal.isPending} erreur={journal.error} onReessayer={() => void journal.refetch()} fonction="L'historique des paramètres">
      {(journal.data ?? []).length === 0 ? (
        <EtatVide titre="Aucune version enregistrée">
          <p>Aucun paramètre n&apos;a encore été modifié dans cette collectivité.</p>
        </EtatVide>
      ) : (
        <Tableau legende="Versions des paramètres" colonnes={colonnes} lignes={journal.data ?? []} cleLigne={(e) => e.id} cleColonnes="versions-parametres" />
      )}
    </EtatRequete>
  );
}

function OngletIdentiteComplet() {
  const collectivite = useCollectivite();
  return (
    <div className={commun.pile}>
      <Panneau titre="Référentiel" niveau={3}>
        <EtatRequete chargement={collectivite.isPending} erreur={collectivite.error} fonction="La fiche de la collectivité">
          {collectivite.data ? (
            <dl className={commun.definitions}>
              <dt>Nom officiel</dt>
              <dd>{collectivite.data.nom}</dd>
              <dt>Code court</dt>
              <dd>{collectivite.data.code_court}</dd>
              <dt>Type</dt>
              <dd>{collectivite.data.type === 'commune' ? 'Commune' : 'Conseil départemental'}</dd>
              <dt>Province</dt>
              <dd>{collectivite.data.province.nom}</dd>
              <dt>Département</dt>
              <dd>{collectivite.data.departement?.nom ?? 'non renseigné'}</dd>
              <dt>Statut</dt>
              <dd>{collectivite.data.fictive ? 'Collectivité fictive (démonstration)' : collectivite.data.statut_deploiement}</dd>
            </dl>
          ) : null}
        </EtatRequete>
      </Panneau>
      <OngletIdentite />
      <EtatVide titre="Logo de la collectivité" icone={ImageOff}>
        <p>
          Le dépôt du logo utilise le service des documents (lot L0-C), qui n&apos;est pas encore disponible sur ce serveur. Les documents produits
          porteront le nom et l&apos;adresse ci-dessus.
        </p>
      </EtatVide>
    </div>
  );
}

export function PageParametres() {
  return (
    <>
      <EnTetePage
        etiquette="Administration"
        titre="Paramètres de la collectivité"
        description="Chaque modification crée une version datée, motivée et journalisée ; les versions précédentes restent consultables."
      />
      <Onglets
        libelle="Rubriques des paramètres"
        onglets={[
          { id: 'identite', libelle: 'Identité', contenu: <OngletIdentiteComplet /> },
          {
            id: 'services',
            libelle: 'Services et organigramme',
            contenu: (
              <EtatVide titre="Services et organigramme" icone={Network}>
                <p>
                  Les services sont enregistrés dans la base (table des services), mais l&apos;API ne propose pas encore leur consultation ni leur
                  saisie. Cet écran s&apos;activera avec les routes correspondantes.
                </p>
              </EtatVide>
            ),
          },
          { id: 'numerotation', libelle: 'Numérotation', contenu: <OngletNumerotation /> },
          { id: 'calendrier', libelle: 'Calendrier', contenu: <OngletCalendrier /> },
          { id: 'notifications', libelle: 'Notifications', contenu: <OngletNotifications /> },
          { id: 'historique', libelle: 'Historique des versions', contenu: <HistoriqueVersions /> },
        ]}
      />
      <p className={commun.na}>
        <Building aria-hidden width={14} height={14} /> Les réglages d&apos;une collectivité n&apos;ont aucun effet sur les autres collectivités.
      </p>
    </>
  );
}
